import { useState } from "react";
import { useLoginMutation } from "../features/auth/authApiSlice";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCredentials } from "../features/auth/authSlice";
import InputBox from "../components/InputBox";
import { FcGoogle } from "react-icons/fc";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { ROUTES } from "../assets/data";

const loginSchema = yup.object().shape({
  username: yup.string()
    .required("Username is required"),
  password: yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .max(16, "Password must be less than 16 characters")
    .matches(/^[A-Za-z0-9!@#$%]+$/, "Password can only contain letters, numbers, and !@#$%")
});

const Login = () => {
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showP, setShowP] = useState(false);
  const [trustCheckbox, setTrustCheckbox] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isValid },
    control
  } = useForm({
    resolver: yupResolver(loginSchema),
    mode: "onChange",
    defaultValues: {
      username: "",
      password: ""
    }
  });

  const onSubmit = async (data) => {
    try {
      const { accessToken } = await login({ username: data.username, password: data.password }).unwrap();
      localStorage.setItem("persist", JSON.stringify(trustCheckbox));
      dispatch(setCredentials({ accessToken }));
      navigate(ROUTES.HOME);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] bg-gradient-to-b from-gray-50 to-gray-100 flex items-start justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Welcome Back</h2>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div className="space-y-2">
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <InputBox 
                    usedFor="username" 
                    boxLabel="Email or Phone Number" 
                    onChange={field.onChange}
                    value={field.value}
                    error={errors.username?.message}
                    color="bg-white"
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <InputBox 
                    usedFor="password" 
                    boxLabel="Password" 
                    onChange={field.onChange}
                    value={field.value}
                    error={errors.password?.message}
                    typeChange={1} 
                    showPassword={showP}
                    color="bg-white"
                  >
                    <div
                      className="material-icons text-neutral-500 text-base absolute top-0 right-2 h-full flex justify-end items-center cursor-pointer border-0 bg-transparent"
                      onClick={() => setShowP(!showP)}
                    >
                      visibility
                    </div>
                  </InputBox>
                )}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="terms" 
                checked={trustCheckbox}
                onCheckedChange={(checked) => setTrustCheckbox(checked)}
              />
              <label
                htmlFor="terms"
                className="text-sm text-gray-600"
              >
                Remember Me
              </label>
            </div>

            <div className="space-y-2">
              <Button 
                type="submit"
                disabled={!isValid || isLoading}
                className="w-full h-9 bg-neutral-900 text-white text-sm rounded-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sign In
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-gray-500">or continue with</span>
                </div>
              </div>

              <Button 
                type="button"
                className="w-full h-9 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 text-sm"
              >
                <FcGoogle className="w-4 h-4" />
                <span>Sign in with Google</span>
              </Button>

              <div className="text-center text-sm text-gray-500">
                Don't have an account?{" "}
                <Link to={ROUTES.SIGNUP} className="text-neutral-900 hover:underline">
                  Register
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
